// Sandbox routing-pass plumbing (WP1).
//
// WP0's front door mints signed routing passes and hands them to gated hosts in
// a URL FRAGMENT (`/login#prime_pass=<token>`) so the token never reaches server
// logs or Referer headers. This module owns the client side of that contract:
//
// - `readPassFromLocation()` captures the fragment pass, persists it to
//   sessionStorage (so in-app navigation and reloads keep it), then strips the
//   fragment for a clean URL. Falls back to sessionStorage when the fragment is
//   absent, and degrades to an in-memory copy when sessionStorage throws
//   (private-mode / storage-disabled edge).
// - `verifySandboxPass(api, pass)` POSTs /sandbox/verify-pass, memoized per pass
//   value so guard re-renders never re-POST the same token.
// - `clearSandboxPass()` drops the stored pass (used after a successful login).

const STORAGE_KEY = 'prime.sandboxPass';

let inMemoryPass = null;
const verifyCache = new Map();

function readStoredPass() {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // sessionStorage unavailable — fall through to the in-memory copy.
  }
  return inMemoryPass;
}

function storePass(pass) {
  inMemoryPass = pass;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, pass);
  } catch {
    // Private-mode edge: the in-memory copy above keeps the pass alive for
    // this document; a full reload will need the fragment again.
  }
}

/**
 * Read the routing pass for this host.
 *
 * Order matters: persist FIRST, then strip the fragment, so a storage failure
 * mid-strip can never lose the pass.
 *
 * @returns {string|null} the pass token, or null when none is available.
 */
export function readPassFromLocation() {
  if (typeof window === 'undefined') return null;
  const rawHash = String(window.location.hash || '');
  const params = new URLSearchParams(rawHash.startsWith('#') ? rawHash.slice(1) : rawHash);
  const fragmentPass = params.get('prime_pass');
  if (fragmentPass) {
    storePass(fragmentPass);
    try {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}`,
      );
    } catch {
      // A failed strip only leaves the fragment visible; the pass is already
      // persisted, so continue.
    }
    return fragmentPass;
  }
  return readStoredPass();
}

/**
 * Verify a routing pass against the API. Memoized per pass value: concurrent
 * and repeated calls for the same token share one POST.
 *
 * @param {{ post: Function }} api - the app api client (or a compatible mock).
 * @param {string|null} pass
 * @returns {Promise<{ valid: boolean, identity: { email: string, displayName?: string }|null }>}
 */
export function verifySandboxPass(api, pass) {
  if (!pass) return Promise.resolve({ valid: false, identity: null });
  if (!verifyCache.has(pass)) {
    const pending = Promise.resolve()
      .then(() => api.post('/sandbox/verify-pass', { pass }))
      .then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        return {
          valid: payload?.valid === true,
          identity: payload?.valid === true ? payload?.identity || null : null,
        };
      })
      .catch(() => {
        // Do not poison the memo with a transient network failure — a later
        // call (e.g. after connectivity returns) may retry.
        verifyCache.delete(pass);
        return { valid: false, identity: null };
      });
    verifyCache.set(pass, pending);
  }
  return verifyCache.get(pass);
}

/** Drop the stored pass (call after a successful sign-in). */
export function clearSandboxPass() {
  inMemoryPass = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

// Test-only: reset module-level state between cases.
export function __resetSandboxPassForTests() {
  inMemoryPass = null;
  verifyCache.clear();
}
